import { nextTick, onMounted, watch, type Ref } from 'vue'
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router'

/**
 * Two-way sync between a container's filter ref and the URL query string.
 *
 * Used by treasury list / directory containers so an applied filter set
 * becomes a shareable link — the operator can paste a URL into Slack and
 * the recipient lands on the same filtered view. Refreshing the page no
 * longer wipes filters either.
 *
 * Why a small composable instead of folding this into DataTableV2:
 * every container owns a different filter shape (status enums, date
 * ranges, decimal amount strings, free-text investor name/email).
 * One-size-fits-all serialization would either lose type information
 * or force every consumer through a generic schema. Instead the
 * mechanics (watch + serialize + `router.replace`, plus seed-from-URL
 * on mount) live here, and each consumer supplies `toQuery` /
 * `fromQuery` so it can validate enum values, parse dates, etc.
 *
 * Non-filter URL params (notably `page` / `pageSize` owned by
 * DataTableV2's `urlSync`) are preserved — this composable only touches
 * the keys it emits via `toQuery`.
 *
 * Echo-loop guard: when seeding from URL on mount, the next emission
 * of the deep watcher is suppressed so the seeding step doesn't
 * immediately re-push the URL it just read.
 *
 * Outside a routed context (`useRoute`/`useRouter` throw): the
 * composable no-ops via try/catch, so consumer containers can still be
 * unit-tested in isolation.
 *
 * @example
 *   useFilterUrlSync(filterValues, {
 *     toQuery: (state) => ({
 *       status: state.status ?? undefined,
 *       fromDate: state.fromDate ? state.fromDate.toISOString() : undefined,
 *     }),
 *     fromQuery: (query) => {
 *       const seed: Partial<MyFilters> = {}
 *       if (
 *         typeof query.status === 'string' &&
 *         Object.values(MY_STATUS).includes(query.status as MyStatus)
 *       ) {
 *         seed.status = query.status as MyStatus
 *       }
 *       if (typeof query.fromDate === 'string') {
 *         const d = new Date(query.fromDate)
 *         if (!Number.isNaN(d.getTime())) seed.fromDate = d
 *       }
 *       return seed
 *     },
 *     onFiltersChange: () => {
 *       currentPage.value = 1
 *     },
 *   })
 */
export interface FilterUrlSyncOptions<T> {
  /**
   * Convert filter state → URL query updates. Return `undefined` for keys
   * that should be REMOVED from the URL (empty string, null, default value).
   */
  toQuery: (state: T) => Record<string, string | undefined>
  /**
   * Convert URL query → partial filter state to merge in on mount. Each
   * field should validate the URL string before assigning — operators can
   * paste a URL with a bogus enum value.
   */
  fromQuery: (query: Record<string, unknown>) => Partial<T>
  /**
   * Called after the URL is pushed when filters change — typically used
   * to reset pagination so the operator doesn't land on an empty page
   * beyond the new result set.
   */
  onFiltersChange?: () => void
}

export function useFilterUrlSync<T extends Record<string, unknown>>(
  filterRef: Ref<T>,
  options: FilterUrlSyncOptions<T>,
): void {
  type RouteLike = ReturnType<typeof useRoute> | null
  type RouterLike = ReturnType<typeof useRouter> | null
  let route: RouteLike = null
  let router: RouterLike = null
  try {
    route = useRoute()
    router = useRouter()
  } catch {
    route = null
    router = null
  }

  if (!route || !router) return

  // Echo-loop guard: when seeding from URL, suppress the next watcher
  // emission so the seeding step doesn't immediately re-push the URL.
  let suppressNextWatch = false

  onMounted(() => {
    const seed = options.fromQuery({ ...route.query })
    const seedKeys = Object.keys(seed) as Array<keyof T>
    if (seedKeys.length === 0) return
    suppressNextWatch = true
    const next = { ...filterRef.value } as T
    for (const key of seedKeys) {
      const value = seed[key]
      if (value !== undefined) {
        next[key] = value as T[keyof T]
      }
    }
    filterRef.value = next
  })

  watch(
    filterRef,
    async (state) => {
      if (suppressNextWatch) {
        suppressNextWatch = false
        return
      }
      const updates = options.toQuery(state)
      // Merge updates into the existing query; any key whose value is
      // `undefined` is REMOVED from the merged query.
      const nextQuery: Record<string, unknown> = { ...route.query }
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined) {
          delete nextQuery[key]
        } else {
          nextQuery[key] = value
        }
      }
      await nextTick()
      await router.replace({ query: nextQuery as LocationQueryRaw })
      options.onFiltersChange?.()
    },
    { deep: true },
  )
}
